import React from 'react';
import { Box, Typography, Button, Tooltip, useMediaQuery } from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';
import MoreHorizIcon from '@mui/icons-material/MoreHoriz';
import { StatusChip } from '../ui';
import { formatCurrency } from '../../utils/currencyFormatter';
import { radius } from '../../themes/smartinventoryTheme';

const rowBorderSx = { borderBottom: '1px solid', borderColor: 'divider', '&:last-of-type': { borderBottom: 'none' } };

// One compact row per order (CHANGES.md §5.3 — status, customer, items, note
// and total together). Everything but the action buttons opens the full
// item/discount/note breakdown (OrderDetailDialog, shared with the
// customer-facing My Orders page) — the note column here is `noWrap` so a
// long one needs the dialog to read in full anyway. That clickable region is
// its own element (not the whole row) so the real <Button>s below aren't
// nested inside another `role="button"`, which both breaks screen-reader
// semantics and would make an accessible-name query like "Mark as paid"
// match two elements. Only the action valid for the order's current state
// renders (§5.2) — same handlers/guards as before, just fewer buttons drawn
// per row. Invoice-managed payments stay a disabled note, never a button
// (§5.4 — preserves the 409 behaviour).
const ClientOrderRow = ({ t, i18n, order, onView, onAccept, onReject, onMarkPaid, onMarkUnpaid, paymentPending, tourAnchor }) => {
  // Below the theme's `sm` breakpoint (900px) the one-line layout squeezes
  // the order number, customer and note down to nothing, so the row stacks:
  // the clickable summary on top, actions on their own line. A literal query
  // (matching DataTableCard) keeps this working in specs without a ThemeProvider.
  const isMobile = useMediaQuery('(max-width:899.95px)');
  const initials = (order.username || '?').slice(0, 2).toUpperCase();
  const itemsCount = order.orderItems?.length
    ? order.orderItems.reduce((sum, it) => sum + (it.quantity || 0), 0)
    : (order.quantity || 0);
  const isInvoiced = Boolean(order.invoiceId);
  const isPending = order.status === 'pending';
  const isAccepted = order.status === 'accepted';
  const isPaid = order.status === 'paid';
  const isRejected = order.status === 'rejected';
  const note = isRejected && order.rejectReason ? `${t('Reject Reason')}: ${order.rejectReason}` : (order.message || '');

  const avatar = (
    <Box sx={{ width: 38, height: 38, borderRadius: `${radius.md}px`, bgcolor: 'action.selected', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
      <Typography variant="caption" color="text.secondary" fontWeight={600}>{initials}</Typography>
    </Box>
  );

  const summary = (
    <Box sx={{ flex: 1.6, minWidth: 0 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25, rowGap: 0.5, flexWrap: isMobile ? 'wrap' : 'nowrap' }}>
        <Typography variant="subtitle1" noWrap>{t('Order #')}{order.orderNumber}</Typography>
        <StatusChip status={order.status} label={t(order.status.charAt(0).toUpperCase() + order.status.slice(1))} />
      </Box>
      <Typography variant="body2" color="text.secondary" noWrap>
        {order.username} · {order.createdAt ? new Date(order.createdAt).toLocaleDateString() : t('N/A')} · {t('{{count}} items', { count: itemsCount, defaultValue: `${itemsCount} items` })}
      </Typography>
      {isMobile && note && (
        <Typography variant="body2" color="text.secondary" noWrap>{note}</Typography>
      )}
    </Box>
  );

  const total = (
    <Typography variant="subtitle1" sx={isMobile ? { flexShrink: 0, textAlign: 'right' } : { width: 90, textAlign: 'right' }}>
      {formatCurrency(order.totalPrice, 'USD', i18n.language)}
    </Typography>
  );

  const actions = (
    <Box sx={{ display: 'flex', gap: 1, flexShrink: 0, justifyContent: 'flex-end', ...(isMobile ? { flexWrap: 'wrap' } : { width: 220 }) }}>
      {isPending && (
        <>
          <Button size="small" variant="contained" color="success" startIcon={<CheckCircleIcon fontSize="small" />} onClick={() => onAccept(order)}>
            {t('Accept', { defaultValue: 'Accept' })}
          </Button>
          <Button size="small" variant="outlined" color="error" startIcon={<CancelIcon fontSize="small" />} onClick={() => onReject(order)}>
            {t('Reject', { defaultValue: 'Reject' })}
          </Button>
        </>
      )}
      {isAccepted && (
        isInvoiced ? (
          <Tooltip title={t('Payment is managed by invoice {{invoiceRef}}', { invoiceRef: order.invoiceNumber || order.invoiceId })}>
            <span>
              <Button size="small" variant="outlined" disabled>{t('Managed by invoice')}</Button>
            </span>
          </Tooltip>
        ) : (
          <Button size="small" variant="outlined" disabled={paymentPending} onClick={() => onMarkPaid(order)}>
            {paymentPending ? t('Marking as paid…') : t('Mark as paid')}
          </Button>
        )
      )}
      {isPaid && !isInvoiced && (
        <Button size="small" variant="outlined" color="warning" disabled={paymentPending} onClick={() => onMarkUnpaid(order)}>
          {paymentPending ? t('Marking as unpaid…') : t('Mark as unpaid')}
        </Button>
      )}
      {(isRejected || (isPaid && isInvoiced)) && (
        <MoreHorizIcon fontSize="small" sx={{ color: 'text.disabled' }} />
      )}
    </Box>
  );

  const viewProps = {
    onClick: () => onView(order),
    role: 'button',
    tabIndex: 0,
    onKeyDown: (e) => { if (e.key === 'Enter') onView(order); },
    'aria-label': t('View order', { defaultValue: 'View order' }),
  };

  if (isMobile) {
    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, px: 2, py: 1.5, ...rowBorderSx }}>
        <Box
          {...viewProps}
          sx={{
            display: 'flex', alignItems: 'flex-start', gap: 1.5, cursor: 'pointer',
            borderRadius: `${radius.md}px`, mx: -1, px: 1, py: 0.5, '&:hover': { bgcolor: 'action.hover' },
          }}
        >
          {avatar}
          {summary}
          {total}
        </Box>
        {actions}
      </Box>
    );
  }

  return (
    <Box data-tour={tourAnchor ? 'orders-row' : undefined} sx={{ display: 'flex', alignItems: 'center', gap: 2.25, px: 3, py: 2, ...rowBorderSx }}>
      <Box
        {...viewProps}
        sx={{
          display: 'flex', alignItems: 'center', gap: 2.25, flex: 1, minWidth: 0, cursor: 'pointer',
          borderRadius: `${radius.md}px`, mx: -1, px: 1, py: 0.5, '&:hover': { bgcolor: 'action.hover' },
        }}
      >
        {avatar}
        {summary}
        <Box sx={{ flex: 1.2, minWidth: 0 }}>
          <Typography variant="body2" color="text.secondary" noWrap>{note}</Typography>
        </Box>
        {total}
      </Box>
      {actions}
    </Box>
  );
};

export default ClientOrderRow;
